import { css } from '@emotion/react'
import { Theme } from '@mui/material/styles'

export const hazardBoxFormContainer = ({ theme }: { theme: Theme }) => css`
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  flex-direction: column;
  height: 100%;
  gap: ${theme.spacing(2)};
`

export const hazardBoxYearSlider = ({ theme }: { theme: Theme }) => css`
  padding: ${theme.spacing(1)} ${theme.spacing(4)};
  width: 100%;
`

export const hazardBoxInfo = ({ theme }: { theme: Theme }) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing(1)};
  padding-bottom: 0;
`

export const hazardBoxSectionInfo = ({ theme }: { theme: Theme }) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing(1)} 0;
  width: 100%;
  color: ${theme.palette.text.primary};
`

export const hazardBoxSLRSliderContainer = ({
  theme,
  hasSetValue,
}: {
  theme: Theme
  hasSetValue: boolean
}) => css`
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;
  background-color: ${theme.palette.background.paper};
  box-sizing: border-box;

  align-self: baseline;

  flex-grow: 1;
  grid-column: span 2;
  border: ${hasSetValue
    ? '2px' + ' solid' + theme.palette.primary.main
    : '1px' + ' solid' + theme.palette.grey[100]};
  transition: border 0.15s;

  gap: ${theme.spacing(2)};
  padding: 0;
`

export const hazardBoxCheckboxContainer = ({
  theme,
  hasSetValue,
}: {
  theme: Theme
  hasSetValue: boolean
}) => css`
  display: flex;
  flex-direction: column;
  height: auto;
  box-sizing: border-box;
  height: 100%;
  background-color: ${theme.palette.white.main};
  flex-shrink: 1;
  margin: ${theme.spacing(2)} 0;
  border: ${hasSetValue
    ? '2px' + ' solid' + theme.palette.primary.main
    : '1px' + ' solid' + theme.palette.grey[100]};
  transition: border 0.15s;

  gap: ${theme.spacing(2)};
`

export const hazardBoxRadioGroupContainer = ({
  theme,
  hasSetValue,
  height,
}: {
  theme: Theme
  hasSetValue: boolean
  height: number
}) => css`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: auto;
  background-color: ${theme.palette.white.main};

  grid-row-end: ${height > 212 ? 'span 2' : ''};

  flex-shrink: 1;
  padding: 0;
  max-height: min-content;
  box-sizing: border-box;

  border: ${hasSetValue
    ? '2px' + ' solid' + theme.palette.primary.main
    : '1px' + ' solid' + theme.palette.grey[100]};
  transition: border 0.15s;

  gap: ${theme.spacing(2)};
  div:first-of-type {
    margin-top: 0 !important;
  }
`

export const hazardBoxSLRSlider = ({ theme }: { theme: Theme }) => css`
  padding: ${theme.spacing(2)} ${theme.spacing(8)};
  padding-top: ${theme.spacing(3)};
  width: 100%;
  overflow: hidden;

  p {
    font-size: 12px !important;
  }
`

export const sectionTitle = css`
  font-weight: 700;
`

export const radioGroupContent = ({ theme }: { theme: Theme }) => css`
  margin-top: ${theme.spacing(1)};
  margin-left: ${theme.spacing(2)};
  margin-bottom: ${theme.spacing(1)};
`

export const checkboxLabel = ({ theme, disabled }: { theme: Theme; disabled: boolean }) => css`
  margin-right: 8px;
  color: ${disabled ? theme.palette.text.disabled : theme.palette.text.primary};
`

export const imageBox = ({ theme }: { theme: Theme }) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: ${theme.spacing(140)};
  border-radius: ${theme.shape.borderRadius}px;
  border: 1px solid ${theme.palette.grey[100]};
  margin: ${theme.spacing(4)} 0;
`

export const fullHeight = () => css`
  height: 100%;
`

export const bold700 = css`
  font-weight: 700;
`

export const hazardDetailFormContainer = (theme: Theme) => css`
  width: 100%;
  height: 100%;
  display: flex;
  gap: 1px;
  flex-direction: column;
  align-items: stretch;
  background-color: ${theme.palette.grey[100]};

  > div {
    opacity: 1 !important;
  }
`

export const hazardControlsRow = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 0;
`

export const disclaimerTopText = ({ theme }: { theme: Theme }) => css`
  font-size: 12px;
  color: ${theme.palette.primary.main};
  padding: ${theme.spacing(1)} ${theme.spacing(3)};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

export const hazardControlsColumn = css`
  display: flex;
  flex-direction: column;
  padding-top: 8px;
`

export const accordionHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  padding-right: 8px;
`

export const accordionHeaderParent = css`
  position: relative;
  width: 100%;
  height: 100%;
`

export const tabsStyle = ({ theme }: { theme: Theme }) => css`
  border-width: 0px;
  box-shadow: none;
  background-color: ${theme.palette.background.paper};
  > .MuiBox-root {
    border-bottom: 0px;

    > .MuiTabs-root {
      box-shadow: none;
    }
  }
  > .MuiBox-root[role='tabpanel'] {
    background-color: transparent !important;
    padding: 8px;
  }

  .MuiTabs-root {
    min-height: 0;
  }

  button.MuiTab-root[role='tab'] {
    text-transform: none;
    padding: 8px 0;
    letter-spacing: 0;
    min-height: 0;
  }

  .reset-filters {
    color: ${theme.palette.primary.main};
    text-align: right;
    font-size: 14px;
    font-weight: 600;
    line-height: 100%;
    letter-spacing: -0.132px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`

export const scenarioAccordionStyle = ({ theme }: { theme: Theme }) => css`
  background-color: transparent;
  flex-shrink: 1;

  > .MuiAccordionSummary-root {
    border-radius: 4px;
    background-color: ${theme.palette.primary[900]};
  }

  .MuiAccordionDetails-root {
    padding: 0;
  }
`

export const fauxAccordionHeader = ({ theme }: { theme: Theme }) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 32px;
  border-radius: 4px;
  background-color: ${theme.palette.primary.dark}20;
  padding-left: ${theme.spacing(2)};
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;

  svg {
    color: ${theme.palette.primary.dark};
  }
`
